import { useState, useEffect, useCallback } from "react";
import { fetchArtieJobs, fetchArtieJob } from "../api";

// Classification → color (matches the four ARTIE labels)
const CLASS_COLORS = {
  "No further coordination needed": "#3fb950",
  "Needs analyst review": "#d29922",
  "Likely more coordination needed": "#f85149",
  "Insufficient information": "#8b949e",
};

const STATUS_COLORS = {
  done: "#3fb950",
  running: "#58a6ff",
  queued: "#d29922",
  error: "#f85149",
};

// Triage prompt (mode) → label / color
const PROMPT_LABELS = { case_status: "Case Status", vul_status: "Vul Status" };
const PROMPT_COLORS = { case_status: "#58a6ff", vul_status: "#a371f7" };
const promptLabel = (p) => PROMPT_LABELS[p] || "Case Status";

// Vul Status priority → color
const PRIORITY_COLORS = { P1: "#f85149", P2: "#d29922", P3: "#8b949e" };
// Dimension rating → color
const RATING_COLORS = { strong: "#f85149", moderate: "#d29922", weak: "#3fb950", unknown: "#8b949e" };

// csaf_quality_findings statuses → color
const FINDING_COLORS = {
  clear: "#3fb950",
  consistent: "#3fb950",
  primary_sources_present: "#3fb950",
  none_found: "#3fb950",
  actionable: "#3fb950",
  not_indicated: "#3fb950",
  partial: "#d29922",
  partially_actionable: "#d29922",
  only_secondary_sources: "#d29922",
  possible: "#d29922",
  unclear: "#d29922",
  exploit_or_poc_present: "#f85149",
  active_exploitation_present: "#f85149",
  inconsistent: "#f85149",
  likely: "#f85149",
  not_actionable: "#f85149",
  not_found: "#8b949e",
};

const FINDING_LABELS = {
  publisher: "Publisher",
  tracking: "Tracking",
  affected_products: "Affected products",
  fixed_products_or_remediation: "Remediation",
  product_tree_consistency: "Product tree",
  references: "References",
  exploit_or_active_exploitation: "Exploitation",
  multi_party_or_downstream_impact: "Downstream impact",
  coordination_evidence: "Coordination evidence",
  user_actionability: "User actionability",
};

// Severity → color. Accepts a CVSS number ("9.8") or a label ("Critical").
function sevColor(sev) {
  if (sev == null || sev === "") return "#8b949e";
  const n = parseFloat(sev);
  if (!Number.isNaN(n)) {
    if (n >= 9) return "#f85149";
    if (n >= 7) return "#f0883e";
    if (n >= 4) return "#d29922";
    return "#3fb950";
  }
  const s = String(sev).toLowerCase();
  if (s.includes("critical")) return "#f85149";
  if (s.includes("high")) return "#f0883e";
  if (s.includes("medium") || s.includes("moderate")) return "#d29922";
  if (s.includes("low")) return "#3fb950";
  return "#8b949e";
}

// Remediation status → color. Note "unvalidated" is checked before "validated".
function remediationColor(status) {
  const s = String(status || "").toLowerCase();
  if (s.includes("unvalidated") || s.includes("candidate") || s.includes("mitigation") || s.includes("partial")) return "#d29922";
  if (s.includes("validated") || s.includes("fix")) return "#3fb950";
  if (s.includes("none") || s.startsWith("no")) return "#f85149";
  return "#8b949e";
}

function confirmationColor(c) {
  const s = String(c || "").toLowerCase();
  if (s.includes("disputed")) return "#f85149";
  if (s.includes("confirmed") || s.includes("live")) return "#3fb950";
  return "#8b949e";
}

// Gap-score rubric — mirrors "Coordination Gap Scoring" in
// vinnieapi/guides/artie_triage_prompt.md (keep the two in sync).
const GAP_SCORE_RUBRIC = [
  ["Missing or unclear advisory publisher", 2],
  ["Missing or unclear advisory tracking/status", 2],
  ["No CVE or stable vulnerability identifier", 2],
  ["Affected product/version information missing or unclear", 3],
  ["Product tree missing, incomplete, or inconsistent", 3],
  ["No fixed version, patch, workaround, or mitigation", 3],
  ["Product status doesn't distinguish affected vs fixed", 2],
  ["Multiple vendors/products/components affected", 3],
  ["Dependency / library / supply-chain indicator", 3],
  ["Public exploit or PoC before clear remediation", 2],
  ["Active exploitation mentioned", 3],
  ["Vendor/researcher/coordinator involvement unclear", 2],
  ["Vendor non-response, dispute, or partial fix", 3],
  ["End-of-life product without mitigation", 2],
  ["Only generic CVE/NVD metadata, no primary source", 2],
  ["No user-actionable remediation guidance", 2],
  ["CSAF content and reference text conflict", 2],
];

const GAP_SCORE_BANDS = [
  ["0–2", "No further coordination needed", CLASS_COLORS["No further coordination needed"]],
  ["3–5", "Needs analyst review", CLASS_COLORS["Needs analyst review"]],
  ["6+", "Likely more coordination needed", CLASS_COLORS["Likely more coordination needed"]],
  ["unscorable", "Insufficient information", CLASS_COLORS["Insufficient information"]],
];

function GapScoreModal({ onClose }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 1100,
        background: "rgba(0,0,0,0.55)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "var(--bg-primary)",
          border: "1px solid var(--border)", borderRadius: 8,
          boxShadow: "0 8px 32px rgba(0,0,0,0.5)",
          width: "min(680px, 92vw)", maxHeight: "82vh",
          display: "flex", flexDirection: "column",
        }}
      >
        <div style={{
          display: "flex", alignItems: "center", padding: "12px 16px",
          borderBottom: "1px solid var(--border)", flexShrink: 0,
        }}>
          <span style={{ color: "var(--text-secondary)", fontWeight: 600, fontSize: 14 }}>
            ⓘ Coordination Gap Score
          </span>
          <button
            onClick={onClose}
            style={{
              marginLeft: "auto", background: "transparent", border: "1px solid var(--border)",
              color: "var(--text-secondary)", padding: "2px 10px", borderRadius: 4,
              cursor: "pointer", fontSize: 14,
            }}
          >×</button>
        </div>
        <div style={{ padding: 16, overflowY: "auto", fontSize: 13, lineHeight: 1.5 }}>
          <p style={{ marginTop: 0 }}>
            ARTIE sums points for coordination-gap indicators found in the CSAF
            advisory and reference text. Higher = more evidence that the case
            still needs coordination work.
          </p>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
            {GAP_SCORE_BANDS.map(([range, label, color]) => (
              <span key={range} style={{
                background: `${color}22`, color, border: `1px solid ${color}55`,
                padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
              }}>
                {range} → {label}
              </span>
            ))}
          </div>
          <p style={{ fontSize: 12, color: "var(--text-muted)" }}>
            The model may override the band when evidence clearly supports a
            different label (the override is explained in the summary). The
            per-job "Gap Score Breakdown" section shows which of these
            conditions actually fired, with evidence.
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)", fontSize: 11 }}>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Condition</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Points</th>
              </tr>
            </thead>
            <tbody>
              {GAP_SCORE_RUBRIC.map(([cond, pts]) => (
                <tr key={cond} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "5px 8px" }}>{cond}</td>
                  <td style={{ padding: "5px 8px", textAlign: "right", fontWeight: 700, color: "#f0883e" }}>+{pts}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

function fmtTime(iso) {
  if (!iso) return "—";
  return iso.slice(0, 19).replace("T", " ");
}

function Chip({ text, color }) {
  return (
    <span style={{
      background: `${color}22`, color, border: `1px solid ${color}55`,
      padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
      whiteSpace: "nowrap",
    }}>
      {text}
    </span>
  );
}

function Section({ title, children }) {
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={{
        fontSize: 12, fontWeight: 600, color: "var(--text-secondary)",
        textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: 8,
      }}>
        {title}
      </div>
      {children}
    </div>
  );
}

function JobResult({ job }) {
  const [showGapInfo, setShowGapInfo] = useState(false);
  const [showJson, setShowJson] = useState(false);
  const [copied, setCopied] = useState(false);
  const r = job.result || {};
  const classification = r.classification || "?";
  const clsColor = CLASS_COLORS[classification] || "#8b949e";
  const findings = r.csaf_quality_findings || {};
  const breakdown = r.score_breakdown || [];
  const jsonText = JSON.stringify(r, null, 2);

  const copyJson = () => {
    navigator.clipboard?.writeText(jsonText).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), 1500); },
      () => {}
    );
  };

  return (
    <div>
      {/* Submitted source: uploaded filename and/or CSAF advisory title */}
      {(job.source_file || job.source_title) && (
        <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
          Submitted:{" "}
          {job.source_file && (
            <span style={{ color: "var(--text-secondary)", fontFamily: "monospace" }}>{job.source_file}</span>
          )}
          {job.source_file && job.source_title && " · "}
          {job.source_title && <span style={{ color: "var(--text-secondary)" }}>{job.source_title}</span>}
        </div>
      )}

      {/* Header chips */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <ModeBadge prompt={job.prompt} />
        <span style={{
          background: clsColor, color: "#0d1117", padding: "4px 14px",
          borderRadius: 6, fontWeight: 700, fontSize: 14,
        }}>
          {classification}
        </span>
        <button
          onClick={() => setShowGapInfo(true)}
          title="How the gap score works"
          style={{
            background: "#58a6ff22", color: "#58a6ff", border: "1px solid #58a6ff55",
            padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
            whiteSpace: "nowrap", cursor: "pointer",
          }}
        >
          Gap score: {r.coordination_gap_score ?? "?"} ⓘ
        </button>
        <Chip text={`Confidence: ${r.confidence ?? "?"}`} color="#a371f7" />
        {r.human_review_required && <Chip text="⚠ Human review required" color="#f0883e" />}
        <button
          onClick={() => setShowJson((v) => !v)}
          title="Show the raw JSON the model returned"
          style={{
            marginLeft: "auto", background: showJson ? "#3fb95022" : "transparent",
            color: showJson ? "#3fb950" : "var(--text-secondary)",
            border: `1px solid ${showJson ? "#3fb95055" : "var(--border)"}`,
            padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600,
            whiteSpace: "nowrap", cursor: "pointer", fontFamily: "monospace",
          }}
        >
          {showJson ? "Hide JSON" : "{ } Show JSON"}
        </button>
      </div>
      {showGapInfo && <GapScoreModal onClose={() => setShowGapInfo(false)} />}

      {showJson && (
        <Section title="LLM JSON Output">
          <div style={{ position: "relative" }}>
            <button
              onClick={copyJson}
              style={{
                position: "absolute", top: 8, right: 8, zIndex: 1,
                background: "var(--bg-primary)", color: "var(--text-secondary)",
                border: "1px solid var(--border)", borderRadius: 4,
                padding: "2px 10px", fontSize: 12, cursor: "pointer",
              }}
            >
              {copied ? "Copied ✓" : "Copy"}
            </button>
            <pre style={{
              margin: 0, padding: 12, fontSize: 12, lineHeight: 1.5,
              fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
              background: "var(--bg-secondary)", border: "1px solid var(--border)",
              borderRadius: 6, maxHeight: "50vh", overflow: "auto",
              whiteSpace: "pre", color: "var(--text-primary)",
            }}>
              {jsonText}
            </pre>
          </div>
        </Section>
      )}

      {/* Run metrics: token usage and execution time */}
      {(() => {
        const u = job.usage || {};
        const tin = u.prompt_tokens, tout = u.completion_tokens;
        const total = u.total_tokens ?? ((tin || 0) + (tout || 0));
        const has = tin != null || tout != null || job.elapsed != null;
        if (!has) return null;
        const fmt = (n) => (n == null ? "?" : Number(n).toLocaleString());
        return (
          <div style={{
            fontSize: 12, color: "var(--text-muted)", marginBottom: 14,
            display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center",
          }}>
            {tin != null && <span>Tokens in: <b style={{ color: "var(--text-secondary)" }}>{fmt(tin)}</b></span>}
            {tout != null && <span>Tokens out: <b style={{ color: "var(--text-secondary)" }}>{fmt(tout)}</b></span>}
            {total ? <span>Total: <b style={{ color: "var(--text-secondary)" }}>{fmt(total)}</b></span> : null}
            {job.elapsed != null && <span>Time: <b style={{ color: "var(--text-secondary)" }}>{job.elapsed}s</b></span>}
            {u.cost != null && <span>Cost: <b style={{ color: "var(--text-secondary)" }}>${Number(u.cost).toFixed(4)}</b></span>}
            {job.model && <span style={{ fontFamily: "monospace" }}>{job.model}</span>}
          </div>
        );
      })()}

      {/* CVEs */}
      {(r.cve_ids || []).length > 0 && (
        <div style={{ marginBottom: 14, display: "flex", gap: 6, flexWrap: "wrap" }}>
          {r.cve_ids.map((c) => (
            <a key={c} href={`/cve/${c}`} style={{
              fontFamily: "monospace", fontSize: 13, color: "var(--accent)",
              border: "1px solid var(--border)", borderRadius: 4, padding: "2px 8px",
              textDecoration: "none",
            }}>
              {c}
            </a>
          ))}
        </div>
      )}

      {r.summary && (
        <Section title="Summary">
          <div style={{ fontSize: 14, lineHeight: 1.5 }}>{r.summary}</div>
        </Section>
      )}

      {(r.findings_breakdown || []).length > 0 && (
        <Section title={`Findings Breakdown (${r.findings_breakdown.length})`}>
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)", fontSize: 11 }}>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>ID</th>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Sev</th>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Finding</th>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Remediation</th>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Confirmation</th>
              </tr>
            </thead>
            <tbody>
              {r.findings_breakdown.map((f, i) => (
                <tr key={f.id || i} style={{ borderBottom: "1px solid var(--border)", verticalAlign: "top" }}>
                  <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere", wordBreak: "break-word" }}>
                    {f.id || "—"}
                  </td>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    <span style={{ color: sevColor(f.severity), fontWeight: 700 }}>{f.severity ?? "—"}</span>
                  </td>
                  <td style={{ padding: "6px 8px" }}>
                    <div style={{ fontWeight: 600 }}>{f.title || "—"}</div>
                    {f.coordination_note && (
                      <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>{f.coordination_note}</div>
                    )}
                    {f.affected && (
                      <div style={{ color: "var(--text-muted)", fontFamily: "monospace", fontSize: 11, marginTop: 2 }}>{f.affected}</div>
                    )}
                    {f.evidence && (
                      <div style={{ color: "var(--text-muted)", fontFamily: "monospace", fontSize: 11, marginTop: 2 }}>↳ {f.evidence}</div>
                    )}
                  </td>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    <span style={{ color: remediationColor(f.remediation_status), fontWeight: 600, fontSize: 12 }}>
                      {f.remediation_status || "—"}
                    </span>
                  </td>
                  <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                    <span style={{ color: confirmationColor(f.confirmation), fontWeight: 600, fontSize: 12 }}>
                      {f.confirmation || "—"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {Object.keys(findings).length > 0 && (
        <Section title="CSAF Quality Findings">
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <tbody>
              {Object.entries(findings).map(([key, f]) => {
                const status = (f && f.status) || "?";
                const color = FINDING_COLORS[status] || "var(--text-secondary)";
                const evidence = (f && f.evidence) || [];
                return (
                  <tr key={key} style={{ borderBottom: "1px solid var(--border)" }}>
                    <td style={{ padding: "6px 8px", color: "var(--text-secondary)", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      {FINDING_LABELS[key] || key}
                    </td>
                    <td style={{ padding: "6px 8px", whiteSpace: "nowrap", verticalAlign: "top" }}>
                      <span style={{ color, fontWeight: 600 }}>{status.replace(/_/g, " ")}</span>
                    </td>
                    <td style={{ padding: "6px 8px", color: "var(--text-muted)", fontSize: 12 }}>
                      {evidence.slice(0, 3).map((e, i) => <div key={i}>{e}</div>)}
                      {evidence.length > 3 && <div>… +{evidence.length - 3} more</div>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Section>
      )}

      {breakdown.length > 0 && (
        <Section title="Gap Score Breakdown">
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)", fontSize: 11 }}>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Condition</th>
                <th style={{ padding: "4px 8px", textAlign: "right" }}>Points</th>
                <th style={{ padding: "4px 8px", textAlign: "left" }}>Evidence</th>
              </tr>
            </thead>
            <tbody>
              {breakdown.map((b, i) => (
                <tr key={i} style={{ borderBottom: "1px solid var(--border)" }}>
                  <td style={{ padding: "6px 8px" }}>{b.condition}</td>
                  <td style={{ padding: "6px 8px", textAlign: "right", fontWeight: 700, color: b.points > 0 ? "#f0883e" : "var(--text-muted)" }}>
                    {b.points > 0 ? `+${b.points}` : b.points}
                  </td>
                  <td style={{ padding: "6px 8px", color: "var(--text-muted)", fontSize: 12 }}>
                    {b.evidence}
                    {b.csaf_path && <div style={{ fontFamily: "monospace", fontSize: 11 }}>{b.csaf_path}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Section>
      )}

      {(r.coordination_concerns || []).length > 0 && (
        <Section title="Coordination Concerns">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
            {r.coordination_concerns.map((c, i) => <li key={i}>{c}</li>)}
          </ul>
        </Section>
      )}

      {(r.recommended_next_actions || []).length > 0 && (
        <Section title="Recommended Next Actions">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6 }}>
            {r.recommended_next_actions.map((a, i) => <li key={i}>{a}</li>)}
          </ul>
        </Section>
      )}

      {r.safe_public_summary && (
        <Section title="Safe Public Summary">
          <div style={{
            fontSize: 13, lineHeight: 1.5, padding: 10,
            background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: 6,
          }}>
            {r.safe_public_summary}
          </div>
        </Section>
      )}

      {r.analyst_thread_note && (
        <Section title="Analyst Note">
          <div style={{ fontSize: 13, lineHeight: 1.5, color: "var(--text-secondary)" }}>
            {r.analyst_thread_note}
          </div>
        </Section>
      )}

      {(r.limitations || []).length > 0 && (
        <Section title="Limitations">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>
            {r.limitations.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </Section>
      )}
    </div>
  );
}

function ModeBadge({ prompt }) {
  const p = prompt || "case_status";
  return <Chip text={promptLabel(p)} color={PROMPT_COLORS[p] || "#58a6ff"} />;
}

function SubmittedLine({ job }) {
  if (!job.source_file && !job.source_title) return null;
  return (
    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 10 }}>
      Submitted:{" "}
      {job.source_file && (
        <span style={{ color: "var(--text-secondary)", fontFamily: "monospace" }}>{job.source_file}</span>
      )}
      {job.source_file && job.source_title && " · "}
      {job.source_title && <span style={{ color: "var(--text-secondary)" }}>{job.source_title}</span>}
    </div>
  );
}

function RunMetrics({ job }) {
  const u = job.usage || {};
  const tin = u.prompt_tokens, tout = u.completion_tokens;
  const total = u.total_tokens ?? ((tin || 0) + (tout || 0));
  if (tin == null && tout == null && job.elapsed == null) return null;
  const fmt = (n) => (n == null ? "?" : Number(n).toLocaleString());
  return (
    <div style={{ fontSize: 12, color: "var(--text-muted)", marginBottom: 14, display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center" }}>
      {tin != null && <span>Tokens in: <b style={{ color: "var(--text-secondary)" }}>{fmt(tin)}</b></span>}
      {tout != null && <span>Tokens out: <b style={{ color: "var(--text-secondary)" }}>{fmt(tout)}</b></span>}
      {total ? <span>Total: <b style={{ color: "var(--text-secondary)" }}>{fmt(total)}</b></span> : null}
      {job.elapsed != null && <span>Time: <b style={{ color: "var(--text-secondary)" }}>{job.elapsed}s</b></span>}
      {u.cost != null && <span>Cost: <b style={{ color: "var(--text-secondary)" }}>${Number(u.cost).toFixed(4)}</b></span>}
      {job.model && <span style={{ fontFamily: "monospace" }}>{job.model}</span>}
    </div>
  );
}

function JsonToggle({ data }) {
  const [show, setShow] = useState(false);
  const [copied, setCopied] = useState(false);
  const text = JSON.stringify(data, null, 2);
  const copy = () => navigator.clipboard?.writeText(text).then(
    () => { setCopied(true); setTimeout(() => setCopied(false), 1500); }, () => {});
  return (
    <>
      <button
        onClick={() => setShow((v) => !v)}
        title="Show the raw JSON the model returned"
        style={{
          background: show ? "#3fb95022" : "transparent", color: show ? "#3fb950" : "var(--text-secondary)",
          border: `1px solid ${show ? "#3fb95055" : "var(--border)"}`, padding: "2px 10px",
          borderRadius: 12, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap", cursor: "pointer", fontFamily: "monospace",
        }}
      >{show ? "Hide JSON" : "{ } Show JSON"}</button>
      {show && (
        <div style={{ position: "relative", width: "100%", marginTop: 10 }}>
          <button onClick={copy} style={{
            position: "absolute", top: 8, right: 8, zIndex: 1, background: "var(--bg-primary)",
            color: "var(--text-secondary)", border: "1px solid var(--border)", borderRadius: 4,
            padding: "2px 10px", fontSize: 12, cursor: "pointer",
          }}>{copied ? "Copied ✓" : "Copy"}</button>
          <pre style={{
            margin: 0, padding: 12, fontSize: 12, lineHeight: 1.5,
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            background: "var(--bg-secondary)", border: "1px solid var(--border)", borderRadius: 6,
            maxHeight: "50vh", overflow: "auto", whiteSpace: "pre", color: "var(--text-primary)",
          }}>{text}</pre>
        </div>
      )}
    </>
  );
}

function Rating({ label, value }) {
  const v = (value || "unknown").toLowerCase();
  return (
    <span style={{ marginRight: 8, whiteSpace: "nowrap" }}>
      <span style={{ color: "var(--text-muted)" }}>{label} </span>
      <span style={{ color: RATING_COLORS[v] || "var(--text-secondary)", fontWeight: 600 }}>{v}</span>
    </span>
  );
}

function VulStatusResult({ job }) {
  const r = job.result || {};
  const findings = Array.isArray(r.findings) ? r.findings : [];
  const tally = findings.reduce((a, f) => { const p = f.priority || "?"; a[p] = (a[p] || 0) + 1; return a; }, {});
  const escalated = findings.filter((f) => f.human_review_required || (f.hard_escalation_triggers || []).length);

  return (
    <div>
      <SubmittedLine job={job} />

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
        <ModeBadge prompt={job.prompt} />
        {["P1", "P2", "P3"].map((p) => tally[p] ? (
          <span key={p} style={{
            background: `${PRIORITY_COLORS[p]}22`, color: PRIORITY_COLORS[p], border: `1px solid ${PRIORITY_COLORS[p]}55`,
            padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 700,
          }}>{p}: {tally[p]}</span>
        ) : null)}
        <Chip text={`${findings.length} finding${findings.length === 1 ? "" : "s"}`} color="#8b949e" />
        {escalated.length > 0 && <Chip text={`⚠ ${escalated.length} escalation${escalated.length === 1 ? "" : "s"}`} color="#f0883e" />}
        <span style={{ marginLeft: "auto" }}><JsonToggle data={r} /></span>
      </div>

      <RunMetrics job={job} />

      {findings.length > 0 && (
        <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
          <thead>
            <tr style={{ borderBottom: "2px solid var(--border)", color: "var(--text-muted)", fontSize: 11 }}>
              <th style={{ padding: "4px 8px", textAlign: "left" }}>#</th>
              <th style={{ padding: "4px 8px", textAlign: "left" }}>ID</th>
              <th style={{ padding: "4px 8px", textAlign: "left" }}>Pri</th>
              <th style={{ padding: "4px 8px", textAlign: "left" }}>Finding</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f, i) => (
              <tr key={f.id || i} style={{ borderBottom: "1px solid var(--border)", verticalAlign: "top" }}>
                <td style={{ padding: "6px 8px", color: "var(--text-muted)" }}>{f.number ?? i + 1}</td>
                <td style={{ padding: "6px 8px", fontFamily: "monospace", fontSize: 12, overflowWrap: "anywhere", wordBreak: "break-word" }}>{f.id || "—"}</td>
                <td style={{ padding: "6px 8px", whiteSpace: "nowrap" }}>
                  <span style={{
                    background: PRIORITY_COLORS[f.priority] || "#8b949e", color: "#0d1117",
                    padding: "1px 8px", borderRadius: 4, fontWeight: 700, fontSize: 12,
                  }}>{f.priority || "?"}</span>
                </td>
                <td style={{ padding: "6px 8px" }}>
                  <div style={{ fontWeight: 600 }}>{f.title || "—"}</div>
                  {f.justification && <div style={{ color: "var(--text-secondary)", fontSize: 12, marginTop: 2 }}>{f.justification}</div>}
                  <div style={{ fontSize: 11, marginTop: 3 }}>
                    <Rating label="A:" value={f.automatability} />
                    <Rating label="TI:" value={f.technical_impact} />
                    <Rating label="U:" value={f.ubiquity} />
                    {f.soft_adjustment && f.soft_adjustment !== "none" && (
                      <span style={{ color: "var(--text-muted)" }}>· adj: {f.soft_adjustment}</span>
                    )}
                  </div>
                  {(f.hard_escalation_triggers || []).length > 0 && (
                    <div style={{ color: "#f0883e", fontSize: 12, marginTop: 3 }}>⚠ escalation: {f.hard_escalation_triggers.join(", ")}</div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(r.report_escalations || []).length > 0 && (
        <Section title="Report-level Escalations">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 13, lineHeight: 1.6, color: "#f0883e" }}>
            {r.report_escalations.map((e, i) => <li key={i}>{e}</li>)}
          </ul>
        </Section>
      )}

      {(r.limitations || []).length > 0 && (
        <Section title="Limitations">
          <ul style={{ margin: 0, paddingLeft: 20, fontSize: 12, color: "var(--text-muted)", lineHeight: 1.6 }}>
            {r.limitations.map((l, i) => <li key={i}>{l}</li>)}
          </ul>
        </Section>
      )}
    </div>
  );
}

function jobIdFromPath() {
  const m = window.location.pathname.match(/^\/triage\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
}

export default function TriagePanel() {
  const [jobs, setJobs] = useState([]);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(jobIdFromPath);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Keep the URL in sync with the selected job (/triage, /triage/<jobId>) so a
  // job is directly linkable, and follow browser back/forward within the view.
  const selectJob = useCallback((id) => {
    const path = id ? `/triage/${id}` : "/triage";
    if (window.location.pathname !== path) window.history.pushState(null, "", path);
    setSelectedId(id);
  }, []);
  useEffect(() => {
    const onPop = () => {
      if (window.location.pathname.startsWith("/triage")) setSelectedId(jobIdFromPath());
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const loadJobs = useCallback(() => {
    fetchArtieJobs()
      .then((d) => { setJobs(d.jobs || []); setError(null); })
      .catch((e) => setError(e.message));
  }, []);

  useEffect(() => {
    loadJobs();
    const t = setInterval(loadJobs, 30000);
    return () => clearInterval(t);
  }, [loadJobs]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    setDetailLoading(true);
    fetchArtieJob(selectedId)
      .then(setDetail)
      .catch((e) => setDetail({ error: e.message }))
      .finally(() => setDetailLoading(false));
  }, [selectedId]);

  return (
    <div className="panel">
      <div className="panel-header">
        ARTIE Coordination Triage
        <span style={{ fontWeight: "normal", fontSize: 12, color: "var(--text-muted)", marginLeft: 10 }}>
          {jobs.length} recent job(s) · refreshes every 30s
        </span>
      </div>
      <div className="panel-body" style={{ display: "flex", gap: 20, alignItems: "flex-start" }}>
        {/* Jobs list */}
        <div style={{ minWidth: 320, maxWidth: 380, flexShrink: 0 }}>
          {error && <div style={{ color: "#f85149", fontSize: 13, marginBottom: 8 }}>Error: {error}</div>}
          {jobs.length === 0 && !error && (
            <div style={{ color: "var(--text-muted)", fontSize: 13 }}>
              No triage jobs yet. Submit one with <code>askvinnie artie-submit</code>.
            </div>
          )}
          <table style={{ width: "100%", fontSize: 13, borderCollapse: "collapse" }}>
            <tbody>
              {jobs.map((j) => (
                <tr
                  key={j.job_id}
                  onClick={() => selectJob(j.job_id)}
                  style={{
                    borderBottom: "1px solid var(--border)", cursor: "pointer",
                    background: selectedId === j.job_id ? "var(--bg-secondary)" : "transparent",
                  }}
                >
                  <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 12, verticalAlign: "top" }}>
                    {j.job_id}
                    {(j.source_file || j.source_title) && (
                      <div style={{
                        color: "var(--text-muted)", fontSize: 11, marginTop: 2,
                        overflowWrap: "anywhere", wordBreak: "break-word", lineHeight: 1.4,
                      }} title={j.source_file || j.source_title}>
                        {j.source_file || j.source_title}
                      </div>
                    )}
                  </td>
                  <td style={{ padding: "8px 6px", verticalAlign: "top", whiteSpace: "nowrap", textAlign: "right" }}>
                    <div style={{ marginBottom: 3 }}>
                      <span style={{
                        color: PROMPT_COLORS[j.prompt || "case_status"], fontSize: 10, fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.03em",
                      }}>{promptLabel(j.prompt)}</span>
                    </div>
                    <div style={{ color: STATUS_COLORS[j.status] || "var(--text-secondary)", fontWeight: 600, fontSize: 12 }}>
                      {j.status}
                    </div>
                    <div style={{ color: "var(--text-muted)", fontSize: 11, marginTop: 2 }}>
                      {fmtTime(j.submitted_at)}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Detail */}
        <div className="triage-print-area" style={{ flex: 1, minWidth: 0 }}>
          {!selectedId && (
            <div style={{ color: "var(--text-muted)", fontSize: 13, paddingTop: 8 }}>
              Select a job to view the triage assessment.
            </div>
          )}
          {detailLoading && <div style={{ color: "var(--text-muted)", fontSize: 13 }}>Loading…</div>}
          {detail && detail.error && (
            <div style={{ color: "#f85149", fontSize: 13 }}>Error: {detail.error}</div>
          )}
          {detail && !detail.error && detail.status !== "done" && (
            <div style={{ fontSize: 13 }}>
              Job status: <span style={{ color: STATUS_COLORS[detail.status] || "inherit", fontWeight: 600 }}>{detail.status}</span>
              {detail.status === "error" && detail.error && ` — ${detail.error}`}
            </div>
          )}
          {detail && !detail.error && detail.status === "done" && (
            detail.prompt === "vul_status"
              ? <VulStatusResult job={detail} />
              : <JobResult job={detail} />
          )}
        </div>
      </div>
    </div>
  );
}
